import { HttpClient } from "@angular/common/http";
import { Injectable, OnDestroy } from "@angular/core";
import { MsalBroadcastService, MsalService } from "@azure/msal-angular";
import { AuthenticationResult, EventMessage, EventType } from "@azure/msal-browser";
import { Subject, filter, takeUntil } from "rxjs";
import { environment } from "../../environments/environment";
import { userReadWriteGroups } from "../../assets/user-groups";
import { when } from "q"

@Injectable({
    providedIn: "root"
})
export class AuthService implements OnDestroy {
  private readonly _destroying$ = new Subject<void>();
  public isIframe: boolean = false;
  public loggedIn: boolean = false;
  baseGraphUrl = "https://graph.microsoft.com/v1.0";

  graphUser: any;
  graphUserGroups: any;

  constructor(
    private httpClient: HttpClient,
    private msalBroadcastService: MsalBroadcastService,
    private msalService: MsalService,
  ) {
      this.isIframe = window !== window.parent && !window.opener;

      this.setupLoginListeners();
    }

  setupLoginListeners() {
    this.msalService.handleRedirectObservable().pipe(
      takeUntil(this._destroying$)
    ).subscribe(
      (resp) => {
        if (this.getActiveAccount() === null && resp === null) {
          this.msalService.loginRedirect()
        } else {
          if( resp !== null && resp.account !== null) {
          this.msalService.instance.setActiveAccount(resp.account)
          console.log("Active user: " + this.msalService.instance.getActiveAccount())
          this.loggedIn = true;
          this.getGraphUser();
          }
        }
      }
    )
  
    this.msalBroadcastService.msalSubject$.pipe(
      filter((eventMsg: EventMessage) => eventMsg.eventType === EventType.LOGIN_FAILURE),
      takeUntil(this._destroying$)
    )
      .subscribe(eventMsg => {
        console.log("login failure " + JSON.stringify(eventMsg), eventMsg);
        this.loggedIn = false;
      });
  
    this.msalBroadcastService.msalSubject$.pipe(
      filter((eventMsg: EventMessage) => eventMsg.eventType === EventType.LOGIN_SUCCESS),
      takeUntil(this._destroying$)
    )
      .subscribe(eventMsg => {
        const authResult = eventMsg.payload as AuthenticationResult
        this.msalService.instance.setActiveAccount(authResult.account)
        // Check if user has correct tenant and client, else not logged in
        const user = this.getActiveAccount();
        const tokenTenant = user!.idTokenClaims!["tid"];
        const tokenClient = user!.idTokenClaims!["aud"];
        if (!tokenTenant ||
          !tokenClient ||
          tokenTenant !== environment.tenantID ||
          tokenClient !== environment.clientID) {
          // Incorrect client/tenant, NOT logged in
          this.loggedIn = false;
        } else {
          // Correct client/tenant, logged in
          this.loggedIn = true;
        }
      });
  
    this.msalBroadcastService.msalSubject$.pipe(
      filter((eventMsg: EventMessage) => eventMsg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS),
      takeUntil(this._destroying$)
    )
      .subscribe((eventMsg) => {
        this.loggedIn = true;
      })
  
    this.msalBroadcastService.msalSubject$.pipe(
      filter((eventMsg: EventMessage) => eventMsg.eventType === EventType.ACQUIRE_TOKEN_FAILURE),
      takeUntil(this._destroying$)
    )
      .subscribe(eventMsg => {
        if (this.getActiveAccount() == null) {
          this.msalService.loginRedirect();
          this.loggedIn = false;
        }
      });
  }

  redirectToLogin() {
    this.msalService.loginRedirect();
  }

  getActiveAccount() {
    return this.msalService.instance.getActiveAccount()
  }

  getIsLoggedIn(): boolean {
    return this.loggedIn
  }

  logout() {
    this.msalService.logout();
  }

  getUserName() {
    return this.getActiveAccount() ? this.getActiveAccount()?.name : "";
  }

  getUserNameAndEmail() {
    return this.getActiveAccount() ? (this.getActiveAccount()?.name + " (" + this.getActiveAccount()?.username + ")") : "";
  }
    
  getGraphUser() {

    this.httpClient.get(`${this.baseGraphUrl}/me`).subscribe({
      next: (data) => {
        this.graphUser = data;
        when(this.graphUser == data).then(() => this.fetchGraphUserGroups());
      },
      error: (error) => {
        console.error(
          " Http get request to MS Graph failed" + JSON.stringify(error)
        );
      }
    });
  }
    
  fetchGraphUserGroupsHandler(response: any){
      this.graphUserGroups = response;
      console.log("Is DLI", this.hasReadWriteGroups(userReadWriteGroups.DLI));
  }
    
  handleError(error: any){
    console.error(error);
  }
    
  fetchGraphUserGroups() {
    this.httpClient
      .get(`${this.baseGraphUrl}/users/${this.graphUser.id}/memberOf`)
      .subscribe({
        next: this.fetchGraphUserGroupsHandler.bind(this),
        error: this.handleError.bind(this)
      });
  }

  hasReadWriteGroups(groups: string[]) {
    if (this.graphUserGroups?.value) {
      return this.graphUserGroups.value.findIndex((x: { id: string; }) => {
        return groups.includes(x.id);
      }) >= 0;
    } else {
      return false;
    }
  }

  ngOnDestroy() {
    this._destroying$.next();
    this._destroying$.complete();
  }
}
